import * as d3 from 'd3'
import * as React from 'react'
import styled from 'styled-components'
import { BodyText } from '../../../../common/src/components/core'
import { ColorName, Colors, DarkColors } from '../../../../common/src/constants'
import LabelledLineChart, { LabelledLineChartConfig, LabelledLineChartData } from './LabelledLineChart'

export interface DailyTimeseriesChartProps {
  series: { name: string; key: string; color: ColorName }[]
  config?: LabelledLineChartConfig
}

export const defaultConfig: LabelledLineChartConfig = {
  yLabelFormatter: val => '' + val,
  legendFormatter: val => '' + val,
  tooltipFormatter: d3.timeFormat('%b %d, %Y'),
}

export const percentChartConfig: LabelledLineChartConfig = Object.assign({}, defaultConfig, {
  yLabelFormatter: (val: number) => '' + (val * 100).toFixed(1),
  legendFormatter: (val: number) => '' + (val * 100).toFixed(1),
})

class DailyTimeseriesChart<T> extends React.PureComponent<{ data: T } & DailyTimeseriesChartProps> {
  render() {
    const { config } = this.props
    const chartData = getChartData(this.props)

    if (!chartData) {
      return <LoadingDiv>Loading chart data...</LoadingDiv>
    }

    if (chartData.values.length === 0) {
      return <LoadingDiv>No data</LoadingDiv>
    }

    const chartConfig: LabelledLineChartConfig = config == null ? defaultConfig : config!
    return <LabelledLineChart config={chartConfig} data={chartData} />
  }
}

function getChartData(props: any): LabelledLineChartData | undefined {
  const { data } = props
  const series = props.series as DailyTimeseriesChartProps['series']

  if (!series) {
    return undefined
  }

  const filledSeries = series.map(s => Object.assign({}, s, { values: data[s.key] }))
  return filledSeries.some(s => !s.values)
    ? undefined
    : {
        series: series.map(s => ({
          name: s.name,
          color: Colors[s.color],
          darkColor: DarkColors[s.color as keyof typeof DarkColors],
        })),
        values: Array.prototype.concat.apply(
          [],
          filledSeries.map(s =>
            s.values.map(({ date, value }: { date: string; value: number }) => ({
              series: s.name,
              date,
              value,
            }))
          )
        ),
      }
}

const LoadingDiv = styled(BodyText)`
  height: 299px;
`

export default DailyTimeseriesChart
